import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import Loadable from 'react-loadable'
import actions from 'redux/actions'
import { reducers } from 'redux/core'
import { connect } from 'redaction'
import { StickyProvider } from 'sb-sticky'
import { IntersectionObserverConductor } from 'sb-intersection-observer'
import { compose, links, scrollToElement, constants, sentry, object } from 'common/helpers'

import injectGlobals from 'decorators/injectGlobals'

import GlobalHelmet from 'components/GlobalHelmet/GlobalHelmet'
import RequestLoader from 'components/loaders/RequestLoader/RequestLoader'
import ModalConductor from 'components/modal/ModalConductor/ModalConductor'
import AppStoreBanner from 'components/banners/AppStoreBanner/AppStoreBanner'
import RequestTokensUpdater from 'components/RequestTokensUpdater/RequestTokensUpdater'
import DummyLandscapeMobile from 'components/loaders/DummyLandscapeMobile/DummyLandscapeMobile'
import NotificationsConductor from 'components/NotificationsConductor/NotificationsConductor'

import PaymentProcessBanner from 'components/payment/PaymentProcessBanner/PaymentProcessBanner'

import MediaProvider from 'containers/MediaProvider/MediaProvider'

import Page500 from 'pages/500/500'

import './App.scss'
// TODO hack for webpack to order css files concatenation
import 'components/buttons/Button/Button.scss'


const ADAInspector = Loadable({ loader: () => import('./ADAInspector/ADAInspector'), loading: () => null })

class App extends Component {

  static propTypes = {
    me: PropTypes.shape({
      isLoggedIn: PropTypes.bool, // @connect
      gender: PropTypes.string, // @connect
    }),
    location: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
    isNewSubscriptionPaymentProcessEnabled: PropTypes.bool, // @injectGlobals
    isSubscriptionLongProcessingBannerEnabled: PropTypes.bool, // @injectGlobals
  }

  state = {
    isAppError: false,
    isADAInspectorEnabled: false,
  }

  constructor() {
    super()

    this.resolveLinksFromBackend()
    this.trackTimeToInteractive()
  }

  static getDerivedStateFromError(error) {
    return {
      isAppError: true,
      error,
    }
  }

  componentDidMount() {
    const {
      isNewSubscriptionPaymentProcessEnabled, isUnleashEnabled,
      me: { isLoggedIn, gender, mixpanelClientUid, email, id },
      location: { query },
    } = this.props

    if (isUnleashEnabled) {
      console.log('Unleash enabled!')
    }

    if (isLoggedIn) {
      // reset user context if user reload browser
      actions.models.user.setUserContext({
        userId: id,
        email,
        mixpanelId: mixpanelClientUid,
      })

      // check postponed subscription payment
      if (isNewSubscriptionPaymentProcessEnabled) {
        actions.models.payment.postponedSubscriptionProcessing()
      }
    }

    document.body.classList.add(`body-${gender}`)
    document.body.classList.add(`body-${isLoggedIn ? 'auth' : 'no-auth'}`)

    window.__debug__.links = links
    window.__debug__.actions = actions
    window.__debug__.reducers = reducers
    window.addEventListener('resize', this.handleResize)

    const runEnv = object.get(window, '__appConfig__.runEnv')

    if (runEnv !== 'prod' && query.ada) {
      this.setState({
        isADAInspectorEnabled: true,
      })
    }

    this.handleLocation()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  componentWillReceiveProps(props) {
    const { me: { isLoggedIn: newIsLoggedIn, gender: newGender }, location: { pathname: newPathname } } = props
    const { me: { isLoggedIn, gender }, location: { pathname } } = this.props

    // TODO rework it to use with SSR
    if (newGender !== gender) {
      document.body.classList.toggle('body-female')
      document.body.classList.toggle('body-male')
    }

    if (newIsLoggedIn !== isLoggedIn) {
      document.body.classList.toggle('body-no-auth')
      document.body.classList.toggle('body-auth')
    }

    if (newPathname !== pathname) {
      actions.view.productPage.setOldPathname(pathname)

      this.setState({
        isAppError: false,
      })
    }
  }

  componentDidUpdate(props) {
    const { location: { pathname: oldPathname } } = props
    const { location: { pathname } } = this.props

    if (pathname !== oldPathname) {
      const isMoveBetweenProfilePages = /profile\//.test(oldPathname) && /profile\//.test(pathname)

      // TODO this is ductape to scroll smoothly to other anchor then moving between profile pages
      if (isMoveBetweenProfilePages) {
        scrollToElement('userInfo', true, 0, true)
      }
      else {
        scrollToElement('root')
      }

      this.handleLocation()
    }
  }

  componentDidCatch(error, info) {
    let err = (error && error.stack) ? error.stack : error

    if (typeof err !== 'string') {
      err = JSON.stringify(err, null, 2)
    }

    // TODO add "info.componentStack" as stack trace to "err"
    sentry.captureException(err)
  }

  resolveLinksFromBackend() {
    const linksFromBackendKeys = (
      window.__globals__ && window.__globals__.linksFromBackend ? Object.keys(window.__globals__.linksFromBackend) : []
    )
    const hasLinksFromBackend  = linksFromBackendKeys && linksFromBackendKeys.length

    if (hasLinksFromBackend) {
      links.fromBackend = window.__globals__.linksFromBackend
    }
  }

  trackTimeToInteractive() {
    if (typeof window !== 'undefined' && window.startTimeToInteract) {
      const endTimeToInteract = Date.now()
      let value = endTimeToInteract - window.startTimeToInteract

      value = Math.round(value / 100) / 10

      const min = Math.floor(value)
      const max = Math.ceil(value)

      value = `${min}-${max}s`

      console.log(`[mixpanel] Register "Time to Interactive": ${value}`)

      if (typeof object.get(window, 'mixpanel.register') === 'function') {
        window.mixpanel.register({
          'Time to Interactive': value,
        })
      }
    }
  }

  handleLocation = () => {
    const { location: { query } } = this.props

    if ('resubscribe-success' in query) {
      actions.models.modals.open(constants.modals.resubscribe.ResubscribeSuccess)
    }
    else if ('unpause-success' in query) {
      const { unpauseCode, unpauseFreeCase } = query

      actions.models.modals.open(constants.modals.pauseSubscription.UnpauseSuccess, { unpauseCode, unpauseFreeCase })
    }

    if (
      query
      && query.utm_source === 'facebook'
      && query.utm_medium === 'cpa'
      && query.utm_campaign === 'mix_landing'
    ) {
      actions.models.payment.setIsFacebookMixLandingCampaign(true)
    }
  }

  render() {
    const {
      children, location: { pathname },
      isSubscriptionLongProcessingBannerEnabled, gender,
    } = this.props
    const { isAppError, error, isADAInspectorEnabled } = this.state

    if (isAppError) {
      return (
        <Page500 err={error} />
      )
    }

    return (
      <Fragment>
        {/* GlobalHelmet must be above children */}
        <GlobalHelmet gender={gender} pathname={pathname} />
        <MediaProvider>
          {
            isSubscriptionLongProcessingBannerEnabled && (
              <PaymentProcessBanner />
            )
          }
          <RequestTokensUpdater />
          <AppStoreBanner />
          <DummyLandscapeMobile />
          <IntersectionObserverConductor>
            <StickyProvider>
              {/* TODO why do we need key="app" ??? */}
              <div key="app" style={{ height: '100%' }}>
                {children}
              </div>
            </StickyProvider>
            <ModalConductor />
          </IntersectionObserverConductor>
          <RequestLoader />
          <NotificationsConductor />
          {
            isADAInspectorEnabled && (
              <ADAInspector />
            )
          }
        </MediaProvider>
      </Fragment>
    )
  }
}

export default compose(
  injectGlobals(({ getFeatureToggle }) => ({
    isUnleashEnabled: getFeatureToggle('sbUnleashTest'),
    isNewSubscriptionPaymentProcessEnabled: getFeatureToggle(constants.features.newSubscriptionPaymentProcess),
    isSubscriptionLongProcessingBannerEnabled: getFeatureToggle(constants.features.subscriptionLongProcessingBanner),
  })),
  connect({
    me: 'models.me',
    gender: 'models.me.gender',
    location: 'routing.location',
  }),
)(App)
